import { useEffect, useMemo, useState } from 'react';
import { i18n, isTerminal, type PalletState } from '@wms/shared';
import { api } from '../lib/api.js';
import { formatDate, formatLocationCoords, formatQty } from '../lib/format.js';
import { type SortDir, useTableSort } from '../lib/sortRows.js';

const { t } = i18n;

interface PalletLine {
  skuCode: string;
  skuNameLt: string;
  skuNameEn: string | null;
  qty: number;
  originalQty: number;
}

function PalletLineSummary({ line }: { line: PalletLine }) {
  return (
    <>
      <span className="font-mono">{line.skuCode}</span> · {t('pallet.contents.original')}{' '}
      <span className="tabular-nums">{formatQty(line.originalQty)}</span> ·{' '}
      {t('pallet.contents.remaining')} <span className="tabular-nums">{formatQty(line.qty)}</span>
    </>
  );
}

const ALL_STATES: PalletState[] = [
  'EXPECTED',
  'ARRIVED',
  'PARTIALLY_DISPATCHED',
  'DISPATCHED',
  'UNPACKED_TO_STOCK',
  'DAMAGED',
  'REPACKED',
  'LOST',
  'CANCELLED',
];

interface OwnPallet {
  id: string;
  code: string;
  state: PalletState;
  partnerId: string | null;
  expectedAt: string | null;
  arrivedAt: string | null;
  dispatchedAt: string | null;
  depletedAt: string | null;
  count: number;
  weightKg: string | null;
  comment: string | null;
  locationRow: string | null;
  locationShelf: string | null;
  locationPosition: string | null;
  unpacked: boolean;
  unpackedAt: string | null;
  lines: PalletLine[];
}

const locationKey = (p: OwnPallet) =>
  [p.locationRow, p.locationShelf, p.locationPosition].filter(Boolean).join('-');

const awaitsDepletionConfirmation = (p: OwnPallet) =>
  !p.depletedAt &&
  !isTerminal(p.state) &&
  p.lines.length > 0 &&
  p.lines.every((line) => line.qty <= 0);

const PALLET_SORT = {
  code: (p: OwnPallet) => p.code,
  state: (p: OwnPallet) =>
    p.depletedAt
      ? 'DEPLETED'
      : awaitsDepletionConfirmation(p)
        ? 'AWAITING_INSPECTION'
        : p.unpacked
          ? 'UNPACKED'
          : p.state,
  location: locationKey,
  expected: (p: OwnPallet) => p.expectedAt,
  arrived: (p: OwnPallet) => p.arrivedAt,
  dispatched: (p: OwnPallet) => p.dispatchedAt,
  count: (p: OwnPallet) => p.count,
  comment: (p: OwnPallet) => p.comment,
} as const;

interface SortCtl {
  sortKey: string;
  sortDir: SortDir;
  toggle: (key: string) => void;
}

function SortableTh({
  label,
  col,
  sort,
  align,
}: {
  label: string;
  col: string;
  sort: SortCtl;
  align?: 'right';
}) {
  const active = sort.sortKey === col;
  return (
    <th className={`px-3 py-2 font-medium ${align === 'right' ? 'text-right' : ''}`}>
      <button
        type="button"
        onClick={() => sort.toggle(col)}
        className={`inline-flex items-center gap-1 hover:text-foreground ${align === 'right' ? 'flex-row-reverse' : ''}`}
        aria-label={label}
      >
        <span>{label}</span>
        <span className="text-[10px] text-muted-foreground">
          {active ? (sort.sortDir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

/**
 * Partner-tier "warehouse" — the same per-pallet information the storage
 * Customer sees in their warehouse (code, state, location, dates, count,
 * comment), scoped server-side to the partner's own pallets. Read-only:
 * unpacking into stock is operator work.
 */
export function MyPalletsPage() {
  const [pallets, setPallets] = useState<OwnPallet[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stateFilter, setStateFilter] = useState<PalletState | ''>('');
  const [rowFilter, setRowFilter] = useState('');
  const [shelfFilter, setShelfFilter] = useState('');
  const [arrivedFrom, setArrivedFrom] = useState('');
  const [arrivedTo, setArrivedTo] = useState('');

  useEffect(() => {
    api<OwnPallet[]>('/partners/me/pallets')
      .then(setPallets)
      .catch((err: unknown) => setError(String(err)));
  }, []);

  const filteredPallets = useMemo(() => {
    if (!pallets) return [];
    const row = rowFilter.trim().toUpperCase();
    const shelf = shelfFilter.trim().toUpperCase();
    return pallets.filter((p) => {
      if (stateFilter && p.state !== stateFilter) return false;
      if (row && (p.locationRow ?? '').toUpperCase() !== row) return false;
      if (shelf && (p.locationShelf ?? '').toUpperCase() !== shelf) return false;
      const arrived = p.arrivedAt?.slice(0, 10) ?? null;
      if (arrivedFrom && (!arrived || arrived < arrivedFrom)) return false;
      if (arrivedTo && (!arrived || arrived > arrivedTo)) return false;
      return true;
    });
  }, [pallets, stateFilter, rowFilter, shelfFilter, arrivedFrom, arrivedTo]);

  const palletSort = useTableSort(filteredPallets, PALLET_SORT, { key: 'code' });
  const physicalCount = filteredPallets.reduce((sum, p) => sum + p.count, 0);

  return (
    <section className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold">{t('my_pallets.title')}</h2>
        <p className="text-sm text-muted-foreground">
          {t('my_pallets.subtitle')}
          {pallets !== null ? (
            <span className="ml-2 tabular-nums">
              · {filteredPallets.length} / {physicalCount}
            </span>
          ) : null}
        </p>
      </div>

      {pallets !== null ? (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <label className="flex items-center gap-2">
            <span className="text-muted-foreground">{t('warehouse.filter.state')}</span>
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value as PalletState | '')}
              className="rounded border border-border bg-background px-2 py-1"
            >
              <option value="">{t('warehouse.filter.all_states')}</option>
              {ALL_STATES.map((state) => (
                <option key={state} value={state}>
                  {t(`pallet.state.${state}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2">
            <span className="text-muted-foreground">{t('location.row')}</span>
            <input
              value={rowFilter}
              onChange={(e) => setRowFilter(e.target.value)}
              maxLength={8}
              className="w-16 rounded border border-border bg-background px-2 py-1 text-center font-mono"
            />
          </label>
          <label className="flex items-center gap-2">
            <span className="text-muted-foreground">{t('location.shelf')}</span>
            <input
              value={shelfFilter}
              onChange={(e) => setShelfFilter(e.target.value)}
              maxLength={8}
              className="w-16 rounded border border-border bg-background px-2 py-1 text-center font-mono"
            />
          </label>
          <label className="flex items-center gap-2">
            <span className="text-muted-foreground">{t('warehouse.filter.arrived_from')}</span>
            <input
              type="date"
              value={arrivedFrom}
              onChange={(e) => setArrivedFrom(e.target.value)}
              className="rounded border border-border bg-background px-2 py-1"
            />
          </label>
          <label className="flex items-center gap-2">
            <span className="text-muted-foreground">{t('warehouse.filter.arrived_to')}</span>
            <input
              type="date"
              value={arrivedTo}
              onChange={(e) => setArrivedTo(e.target.value)}
              className="rounded border border-border bg-background px-2 py-1"
            />
          </label>
          {arrivedFrom || arrivedTo ? (
            <button
              type="button"
              onClick={() => {
                setArrivedFrom('');
                setArrivedTo('');
              }}
              className="rounded border border-border px-2 py-1 text-xs hover:bg-muted"
            >
              {t('common.clear')}
            </button>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
      ) : pallets === null ? (
        <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
      ) : filteredPallets.length === 0 ? (
        <div className="rounded border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
          {t('my_pallets.empty')}
        </div>
      ) : (
        <>
          {/* Desktop table — mirrors the customer warehouse view. */}
          <div className="hidden md:block overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left">
                <tr>
                  <SortableTh label={t('pallet.col.code')} col="code" sort={palletSort} />
                  <SortableTh label={t('pallet.col.state')} col="state" sort={palletSort} />
                  <SortableTh label={t('location.label')} col="location" sort={palletSort} />
                  <SortableTh label={t('pallet.col.expected')} col="expected" sort={palletSort} />
                  <SortableTh label={t('pallet.col.arrived')} col="arrived" sort={palletSort} />
                  <SortableTh
                    label={t('pallet.col.dispatched')}
                    col="dispatched"
                    sort={palletSort}
                  />
                  <SortableTh
                    label={t('pallet.col.count')}
                    col="count"
                    sort={palletSort}
                    align="right"
                  />
                  <SortableTh label={t('pallet.col.comment')} col="comment" sort={palletSort} />
                </tr>
              </thead>
              <tbody>
                {palletSort.sorted.map((p) => (
                  <tr key={p.id} className="border-t border-border align-top">
                    <td className="px-3 py-2 font-mono text-xs">{p.code}</td>
                    <td className="px-3 py-2">
                      <StatusBadge p={p} />
                      {p.unpacked && p.lines.length > 0 ? (
                        <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                          {p.lines.map((l, i) => (
                            <li key={i}>
                              <PalletLineSummary line={l} />
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{formatLocationCoords(p)}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {formatDate(p.expectedAt)}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {formatDate(p.arrivedAt)}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {formatDate(p.dispatchedAt)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{p.count}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{p.comment ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <ul className="md:hidden space-y-2">
            {palletSort.sorted.map((p) => (
              <li key={p.id} className="rounded-md border border-border p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-mono text-sm">{p.code}</span>
                  <StatusBadge p={p} />
                </div>
                <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                  <dt className="text-muted-foreground">{t('location.label')}</dt>
                  <dd className="text-right font-mono">{formatLocationCoords(p)}</dd>
                  <dt className="text-muted-foreground">{t('pallet.col.arrived')}</dt>
                  <dd className="text-right">{formatDate(p.arrivedAt)}</dd>
                  <dt className="text-muted-foreground">{t('pallet.col.count')}</dt>
                  <dd className="text-right tabular-nums">{p.count}</dd>
                  {p.comment ? (
                    <>
                      <dt className="text-muted-foreground">{t('pallet.col.comment')}</dt>
                      <dd className="text-right">{p.comment}</dd>
                    </>
                  ) : null}
                </dl>
                {p.unpacked && p.lines.length > 0 ? (
                  <ul className="space-y-0.5 text-xs text-muted-foreground">
                    {p.lines.map((l, i) => (
                      <li key={i}>
                        <PalletLineSummary line={l} />
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function StatusBadge({ p }: { p: OwnPallet }) {
  return p.depletedAt ? (
    <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
      {t('pallet.depleted.badge')}
    </span>
  ) : awaitsDepletionConfirmation(p) ? (
    <span className="inline-flex items-center rounded-full border border-amber-400 bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
      {t('pallet.depleted.awaiting_badge')}
    </span>
  ) : p.unpacked ? (
    <span className="inline-flex items-center rounded-full border border-green-300 bg-green-100 px-2 py-0.5 text-xs font-medium text-green-900 dark:bg-green-900/40 dark:text-green-100">
      {t('my_pallets.status.unpacked')}
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
      {t(`pallet.state.${p.state}`)}
    </span>
  );
}
